import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useMemo, useState } from 'react';
import { hasValue, EComponentBaseType, useT } from '@progress-fe/core';
import { Box, Button, Center, Flex, Grid, GridItem, IconButton } from '@chakra-ui/react';
import { isClose } from 'is-close';
import { sum, omit } from 'lodash-es';
import { Dialog, Svg } from '../../base';
import { ComponentsLists } from '../../lists';
import { Input, InputNumber, Select } from '../../inputs';
import { JsFieldInspector, JsFieldName } from '../../jsFormBase';
import { JsFormConfigContext } from '../../../contexts';
import { useJsFieldFormData } from '../../../hooks';
/**
 * Component renders "/schemas/jsf-light-fraction-composition" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TFormData.
 */
const LightFractionCompositionJsFieldFC = (props) => {
    const { t } = useT();
    const { components, runCallback, isInspector } = useContext(JsFormConfigContext);
    const { pureList = [] } = components ?? {};
    const [isDictionaryShown, setIsDictionaryShown] = useState(false);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: props.formData.fieldConfig,
        values: props.formData.fieldValues,
        watchPropsChanges: true,
        onChange: props.onChange
    });
    const keys = useMemo(() => new Set(Object.keys(fieldValues?.value ?? [])), [fieldValues]);
    const pureListSelected = pureList.filter((c) => keys.has(c.uuid));
    const hasComponents = pureListSelected.length > 0;
    const selectedComponentsAmount = pureListSelected.length;
    const isFieldInvalid = !!props.rawErrors || selectedComponentsAmount !== keys.size;
    const isManualQuantity = fieldConfig?.isManual;
    const gridTemplate = '100px 120px 120px 120px 120px 24px';
    const selectedOption = useMemo(() => {
        return fieldConfig?.options.find((opt) => opt.value === fieldValues?.optionValue);
    }, [fieldConfig?.options, fieldValues?.optionValue]);
    const sumOfComponents = useMemo(() => {
        const valuesArray = Object.values(fieldValues?.value || {}).map((val) => val?.quantity);
        if (valuesArray.every((v) => v === null)) {
            return undefined;
        }
        const sumV = sum(valuesArray.map((i) => (hasValue(i) ? Number(i) : undefined)));
        const isCloseToOne = !!fieldConfig?.normalization.possible && isClose(1.0, sumV, 0.0000000001);
        return sumV === 1 || isCloseToOne ? 1 : sumV;
    }, [fieldConfig?.normalization.possible, fieldValues?.value]);
    const handleChangeFormOptionValue = (option) => {
        if (!fieldValues) {
            return;
        }
        onChangeFieldValues({
            ...fieldValues,
            optionValue: option.value
        });
    };
    const handleChangeFormValue = (value) => {
        if (!fieldValues) {
            return;
        }
        onChangeFieldValues({
            ...fieldValues,
            value
        });
    };
    const handleAddComponent = (key) => {
        handleChangeFormValue({
            ...fieldValues?.value,
            [key]: null
        });
    };
    const handleChangeValue = (key, value) => {
        const currentValue = fieldValues?.value[key] || {
            temperature: '',
            molarMass: '',
            density: ''
        };
        handleChangeFormValue({
            ...fieldValues?.value,
            [key]: { ...currentValue, quantity: value }
        });
    };
    const handleDeleteComponent = (key) => {
        handleChangeFormValue({ ...omit(fieldValues?.value, [key]) });
    };
    const handleNormalizeCallback = async () => {
        const callbackCode = fieldConfig?.normalization.callbackCode || '';
        if (!callbackCode) {
            return;
        }
        const formDataResult = await runCallback?.(callbackCode);
        if (hasValue(formDataResult)) {
            onChangeFieldConfig(formDataResult.fieldConfig);
            onChangeFieldValues(formDataResult.fieldValues);
        }
    };
    const tabs = useMemo(() => {
        return [
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            }
        ];
    }, [pureList, t]);
    return (_jsxs(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: [_jsx(Box, { "data-testid": "LightFractionCompositionJsField-test", "data-name": props.name, children: _jsxs(Flex, { p: "4px 0 2px 0", flexDirection: "column", gap: "8px", children: [_jsxs(Grid, { gap: '4px', gridTemplateColumns: `minmax(0, 180px) 1fr`, alignItems: 'center', children: [_jsx(JsFieldName, { name: fieldConfig?.title }), _jsx(Select, { name: props.name, options: fieldConfig?.options || [], placeholder: t('common.notSelected'), value: fieldConfig?.options.find((opt) => opt.value === fieldValues?.optionValue), onChange: (newValue) => {
                                        handleChangeFormOptionValue(newValue);
                                    } })] }), hasComponents ? (_jsxs(GridItem, { p: "4px", borderRadius: "4px", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', children: [_jsxs(Box, { overflowY: 'auto', children: [_jsxs(Grid, { gap: "4px", p: "0 0 4px 0", w: "max-content", alignItems: "center", borderBottom: "1px solid", borderColor: "border", templateColumns: gridTemplate, children: [_jsx(GridItem, { children: t('common.component') }), _jsx(GridItem, { children: t('fields.quantity') }), _jsx(GridItem, { children: t('fields.boilingPoint') }), _jsx(GridItem, { children: t('fields.molarMass') }), _jsx(GridItem, { children: t('fields.density') }), _jsx(GridItem, {})] }), _jsx(Box, { p: "6px 0", children: pureListSelected.map((item) => (_jsxs(Grid, { gap: "4px", p: "2px 0", templateColumns: gridTemplate, children: [_jsx(Flex, { align: "center", children: item.name }), _jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", isOnChangeOnlyOnBlur: true, disabled: props.schema.readOnly || !isManualQuantity, rightElement: selectedOption?.unit, value: fieldValues?.value[item.uuid]?.quantity || undefined, onChange: (value) => handleChangeValue(item.uuid, value) }), _jsx(InputNumber, { isFloat: true, size: "xs", variant: "outline", disabled: true, rightElement: fieldConfig?.uom.temperature, value: fieldValues?.value[item.uuid]?.temperature || undefined }), _jsx(InputNumber, { isFloat: true, size: "xs", variant: "outline", disabled: true, rightElement: fieldConfig?.uom.molarMass, value: fieldValues?.value[item.uuid]?.molarMass || undefined }), _jsx(InputNumber, { isFloat: true, size: "xs", variant: "outline", disabled: true, rightElement: fieldConfig?.uom.density, value: fieldValues?.value[item.uuid]?.density || undefined }), _jsx(Box, { position: 'sticky', right: 0, children: _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghost", isDisabled: props.schema.readOnly, icon: _jsx(Svg, { size: "s12", name: "Cross" }), onClick: () => handleDeleteComponent(item.uuid) }) })] }, item.uuid))) })] }), _jsxs(Grid, { gap: "4px", p: "4px 0 1px 0", borderTop: "1px solid", borderColor: "border", templateColumns: "100px 1fr auto", children: [_jsx(Flex, { align: "center", children: t('common.total') }), _jsx(Input, { size: "xs", isDisabled: true, variant: "outline", value: sumOfComponents, isInvalid: sumOfComponents !== 1 }), fieldConfig?.normalization.possible && (_jsx(Button, { size: "xs", variant: "ghost", isDisabled: props.schema.readOnly || fieldConfig?.readOnly, onClick: handleNormalizeCallback, children: t('actions.normalize') }))] })] })) : (_jsx(Center, { h: "32px", color: "bodyText", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', borderRadius: "4px", children: _jsx(Box, { opacity: "0.6", children: t('common.noComponents') }) })), !props.schema.readOnly && (_jsx(Button, { w: "100%", variant: "ghost", size: "smSquare", onClick: () => setIsDictionaryShown(true), children: t('actions.add') }))] }) }), isDictionaryShown && (_jsx(Dialog, { title: t('common.addComponent'), onClose: () => setIsDictionaryShown(false), children: _jsx(ComponentsLists, { tableSx: { height: 300 }, title: '', tabs: tabs, selectedComponentsIds: Array.from(keys.values()), onAddComponent: handleAddComponent, onDeleteComponent: handleDeleteComponent, onClose: () => setIsDictionaryShown(false) }) }))] }));
};
export const LightFractionCompositionJsField = memo(LightFractionCompositionJsFieldFC);
//# sourceMappingURL=LightFractionCompositionJsField.js.map